import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo } from 'react';

import CarouselLoadingWrapper from '@/components/CarouselLoadingWrapper/CarouselLoadingWrapper';
import { CatalogGrid } from '@/components/CatalogCard/CatalogGrid';
import { HomepageAuctionCard } from '@/components/CatalogCard/HomepageAuctionCard/HomepageAuctionCard';
import { useAppSelector } from '@/redux/hooks';
import { getUpcomingAuctionsCount } from '@/redux/modules/auctionCalendar/auctionCalendar.selectors';
import { useAverageLotValueActionCalendarRanking } from '@/utils/amplitude/experiments/hooks/useAverageLotValueActionCalendarRanking';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getAllUpcomingAuctionsUrl } from '@/utils/urls';

import useGetUpcomingCatalogIds from './hooks/useGetUpcomingCatalogIds';

type Props = {
    className?: string;
    shouldUseHomePageRedesign?: boolean;
    testid?: string;
};

export const HomePageUpcomingCatalogs = ({ className, shouldUseHomePageRedesign = false, testid }: Props) => {
    const formatMessage = useAppFormatMessage();
    const catalogIds = useGetUpcomingCatalogIds();
    const upcomingCount = useAppSelector(getUpcomingAuctionsCount);
    const hasCount = upcomingCount !== null; // 0 is a real count

    // Experiment: SEAR-1266 Average Lot Value Auction Calendar Ranking. Only exposure.
    useAverageLotValueActionCalendarRanking();

    const treatmentCards = useMemo(
        () =>
            catalogIds.map((catalogId, index) => ({
                card: (
                    <div
                        className="w-[381px] focus:outline-hidden xsMax:w-[280px]"
                        key={`${catalogId}-online-auctions`}
                    >
                        <HomepageAuctionCard
                            catalogId={catalogId}
                            lazyLoadImages
                            position={index + 1}
                        />
                    </div>
                ),
                key: `${catalogId}-online-auctions`,
            })),
        [catalogIds]
    );

    if (shouldUseHomePageRedesign) {
        return (
            <div
                className={cn('flex flex-col justify-center', className)}
                data-testid={testid}
                id="upcomingCatalogs"
            >
                <div className="relative mx-0 my-xs w-full max-w-full flex-1 self-center pb-md">
                    <CarouselLoadingWrapper>
                        <Carousel
                            action={{
                                path: getAllUpcomingAuctionsUrl(),
                                text: String(formatMessage(['viewAll'])),
                            }}
                            cards={treatmentCards}
                            title={String(
                                hasCount
                                    ? formatMessage(['online_auctions_count'], {
                                          values: {
                                              liveCount: upcomingCount,
                                          },
                                      })
                                    : formatMessage(['online_auctions'])
                            )}
                            useOverlayControls
                        />
                    </CarouselLoadingWrapper>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn('flex flex-col justify-center', className)}
            id="upcomingCatalogs"
        >
            <div className="relative mx-0 my-xs flex flex-1 flex-col self-center overflow-hidden pb-md">
                <H2 className="mb-[20px] text-left xsMax:mb-xs">
                    {hasCount
                        ? formatMessage(['online_auctions_count'], {
                              values: {
                                  liveCount: (
                                      <FormattedNumber
                                          key="online_auctions_count"
                                          style="decimal"
                                          value={upcomingCount}
                                      />
                                  ),
                              },
                          })
                        : formatMessage(['online_auctions'])}
                </H2>
                <CatalogGrid
                    catalogIds={catalogIds}
                    className="mx-xs w-[calc(100%+20px)] max-w-[calc(100%+20px)] flex-[1_0_auto]"
                    lazyLoadImages
                />
                <div
                    className="text-center no-underline [&_button]:inline-block [&_button]:no-underline"
                    data-testid={testid}
                >
                    <Link href={getAllUpcomingAuctionsUrl()}>
                        <Button
                            className="xsMax:mt-sm"
                            variant="outlined"
                        >
                            {formatMessage(['show_all_upcoming_auctions'])}
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
};
